import { Component, type ReactNode, type ErrorInfo } from 'react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@/components/ErrorState';

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={() => this.setState({ hasError: false, error: null })} />;
    }
    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

function ErrorFallback({ error, onReset }: ErrorFallbackProps): React.ReactElement {
  const { t } = useTranslation('aita', { useSuspense: false });

  return (
    <FluentProvider theme={webLightTheme}>
      <ErrorState
        title={t('error.title', 'Something went wrong')}
        body={error?.message ?? t('error.generic', 'An unexpected error occurred.')}
        onRetry={onReset}
        retryLabel={t('error.try_again', 'Try again')}
      />
    </FluentProvider>
  );
}
